// CheckinCard (plan todo 23) — weekly check-in proposal rendered in chat.
// Pending proposals accept/keep via api.engine.respondToWeeklyCheckin through
// the same respondToCheckin controller the Plan screen uses: ALREADY_RESOLVED
// swaps to an inline notice with both buttons disabled. "Explain" sends
// "explain this adjustment" as a normal user message so the agent answers.

import { useState } from "react";
import { CalendarClock } from "lucide-react";
import { cn } from "../../lib/cn";
import { formatNumber } from "../../lib/format";
import {
  checkinRecommendationCopy,
  respondToCheckin,
  type CheckinRespondResult,
} from "../../screens/plan-utils";
import type { WeeklyCheckinData } from "./card-data";
import { CardActionButton, ChatCardShell, OpenInButton } from "./card-ui";

export interface CheckinCardProps {
  readonly data: WeeklyCheckinData;
  readonly onRespondCheckin?: (checkinId: string, accept: boolean) => Promise<unknown>;
  readonly onSendMessage?: (text: string) => void;
  readonly onSwitchScreen?: (screen: string) => void;
}

type Notice = { readonly tone: "success" | "info" | "error"; readonly text: string };

function noticeFor(result: CheckinRespondResult): Notice {
  switch (result.type) {
    case "accepted":
      return { tone: "success", text: "Adjustment accepted — your daily targets were updated." };
    case "kept":
      return { tone: "success", text: "No problem — your current daily target stays the same." };
    case "already-resolved":
      return { tone: "info", text: "This check-in was already resolved. Your latest targets are shown in Plan." };
    default:
      return { tone: "error", text: result.message };
  }
}

export function CheckinCard({
  data,
  onRespondCheckin,
  onSendMessage,
  onSwitchScreen,
}: CheckinCardProps) {
  const [responding, setResponding] = useState(false);
  const [resolved, setResolved] = useState(false);
  const [notice, setNotice] = useState<Notice | null>(null);

  const pending = data.pending;
  const latest = data.adjustments[0];
  const buttonsDisabled = responding || resolved || onRespondCheckin === undefined;

  const handleRespond = (accept: boolean) => {
    if (pending === null || onRespondCheckin === undefined || buttonsDisabled) return;
    setResponding(true);
    setNotice(null);
    void respondToCheckin((acceptedFlag) => onRespondCheckin(pending.id, acceptedFlag), accept).then(
      (result) => {
        setResponding(false);
        setNotice(noticeFor(result));
        if (
          result.type === "accepted" ||
          result.type === "kept" ||
          result.type === "already-resolved"
        ) {
          setResolved(true);
        }
      },
    );
  };

  return (
    <ChatCardShell
      icon={CalendarClock}
      title="Weekly check-in"
      testId="chat-card-checkin"
    >
      {pending !== null ? (
        <div className="flex flex-col gap-3" data-testid="chat-checkin-pending">
          <p className="text-sm leading-relaxed text-text">
            Your estimated daily expenditure changed based on your recent logs.
          </p>
          <p
            className="text-sm font-semibold leading-relaxed text-text"
            data-testid="chat-checkin-recommendation"
          >
            {checkinRecommendationCopy(pending.previousTargetKcal, pending.newTargetKcal)}
          </p>
          {notice !== null && (
            <p
              role={notice.tone === "error" ? "alert" : "status"}
              className={cn(
                "rounded-xl px-3 py-2 text-xs leading-relaxed",
                notice.tone === "success" && "bg-accent-light/60 text-accent-dark",
                notice.tone === "info" && "bg-muted text-text-muted",
                notice.tone === "error" && "bg-red-50 text-danger",
              )}
              data-testid="checkin-notice"
            >
              {notice.text}
            </p>
          )}
          <div className="flex flex-col gap-2">
            {onRespondCheckin !== undefined && (
              <>
                <CardActionButton
                  label={responding ? "Saving…" : "Accept adjustment"}
                  testId="checkin-accept-btn"
                  variant="primary"
                  disabled={buttonsDisabled}
                  onPress={() => handleRespond(true)}
                />
                <CardActionButton
                  label="Keep current target"
                  testId="checkin-keep-btn"
                  variant="secondary"
                  disabled={buttonsDisabled}
                  onPress={() => handleRespond(false)}
                />
              </>
            )}
            {onSendMessage !== undefined && (
              <CardActionButton
                label="Explain"
                testId="checkin-explain-btn"
                variant="ghost"
                onPress={() => onSendMessage("explain this adjustment")}
              />
            )}
            <OpenInButton
              label="Open in Plan"
              screen="plan"
              testId="checkin-open-plan-btn"
              onSwitchScreen={onSwitchScreen}
            />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3" data-testid="chat-checkin-none-pending">
          {latest !== undefined ? (
            <p className="text-sm leading-relaxed text-text" data-testid="chat-checkin-summary">
              {latest.paused
                ? "Your last check-in was paused — your target stayed the same."
                : latest.adjusted
                  ? `Your daily target changed from ${formatNumber(latest.previousTargetKcal)} to ${formatNumber(latest.newTargetKcal)} calories.`
                  : `No change needed — your daily target stayed at ${formatNumber(latest.newTargetKcal)} calories.`}
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-text" data-testid="chat-checkin-summary">
              No check-in is waiting right now. Your first one appears after a full week of logging.
            </p>
          )}
          {onSendMessage !== undefined && latest !== undefined && (
            <CardActionButton
              label="Explain"
              testId="checkin-explain-btn"
              variant="ghost"
              onPress={() => onSendMessage("explain this adjustment")}
            />
          )}
          <OpenInButton
            label="Open in Plan"
            screen="plan"
            testId="checkin-open-plan-btn"
            onSwitchScreen={onSwitchScreen}
          />
        </div>
      )}
    </ChatCardShell>
  );
}
